import React, { useState, useEffect, useRef } from 'react';
import {
    Send, Loader2, MonitorPlay, ChevronLeft, ArrowRight, CheckCircle2,
    Brain, Command, LogOut, Settings, Clock, Paperclip, X, Shield, Upload, Lightbulb, Bell, BookOpen, Calendar, BarChart3, Copy, Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import AdminDashboard from './AdminDashboard';
import ReactMarkdown from 'react-markdown';
import AccountDropdown from './AccountDropdown';
import EvidenceAnalysisModal from './EvidenceAnalysisModal';

const Dashboard = ({ userProfile, onLogout, onUpdateProfile, onSwitchToProduction }) => {
    const [isGuideActive, setIsGuideActive] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [tipIndex, setTipIndex] = useState(0);
    const [, setShowMissingDocs] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [, setHasIncompleteProfile] = useState(false);
    const [showProfileSettings, setShowProfileSettings] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [editName, setEditName] = useState(userProfile.name || '');
    const [editSubject, setEditSubject] = useState(userProfile.subject || '');
    const [editGrade, setEditGrade] = useState(userProfile.grade || '');
    const [profileDocs, setProfileDocs] = useState([]);
    const [showDocUploadModal, setShowDocUploadModal] = useState(false);
    // Evidence modal state - hidden for now, keeping for future use
    // const [showEvidenceModal, setShowEvidenceModal] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(null); // { type, summary, filename }

    // Courses state
    const [courses, setCourses] = useState([]);
    const [newCourse, setNewCourse] = useState({ course_name: '', period: '', grade_level: '', period_length: 50 });
    const [showAddCourse, setShowAddCourse] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null); // { id, course_name, period, grade_level }
    const [copiedMessageId, setCopiedMessageId] = useState(null); // For copy feedback

    // Fetch profile documents on mount
    useEffect(() => {
        const fetchDocs = async () => {
            try {
                const res = await api.get('/get-profile-docs', {
                    params: { email: userProfile.email }
                });
                setProfileDocs(res.data.documents || []);
            } catch (e) {
                console.error('Failed to fetch docs:', e);
            }
        };
        fetchDocs();
    }, [userProfile.email]);

    // Fetch courses on mount
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const res = await api.get('/get-courses', {
                    params: { email: userProfile.email }
                });
                setCourses(res.data.courses || []);
            } catch (e) {
                console.error('Failed to fetch courses:', e);
            }
        };
        fetchCourses();
    }, [userProfile.email]);

    // Course handlers
    const handleAddCourse = async () => {
        if (!newCourse.course_name.trim()) return;
        try {
            const res = await api.post('/add-course', {
                email: userProfile.email,
                ...newCourse,
                is_primary: courses.length === 0 // First course is primary
            });
            setCourses(res.data.courses || []);
            setNewCourse({ course_name: '', period: '', grade_level: '' });
            setShowAddCourse(false);
        } catch (e) {
            console.error('Failed to add course:', e);
        }
    };

    const handleDeleteCourse = async (courseId) => {
        try {
            const res = await api.post('/delete-course', {
                email: userProfile.email,
                course_id: courseId
            });
            setCourses(res.data.courses || []);
        } catch (e) {
            console.error('Failed to delete course:', e);
        }
    };

    const handleSetPrimary = async (courseId) => {
        try {
            const res = await api.post('/set-primary-course', {
                email: userProfile.email,
                course_id: courseId
            });
            setCourses(res.data.courses || []);
        } catch (e) {
            console.error('Failed to set primary:', e);
        }
    };

    const handleUpdateCourse = async () => {
        if (!editingCourse || !editingCourse.course_name.trim()) return;
        try {
            const res = await api.post('/update-course', {
                email: userProfile.email,
                course_id: editingCourse.id,
                course_name: editingCourse.course_name,
                period: editingCourse.period,
                grade_level: editingCourse.grade_level
            });
            setCourses(res.data.courses || []);
            setEditingCourse(null);
        } catch (e) {
            console.error('Failed to update course:', e);
        }
    };

    // Copy message to clipboard
    const handleCopyMessage = async (text, messageId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (e) {
            console.error('Failed to copy:', e);
        }
    };

    // Check which docs are missing for notifications
    const hasSyllabus = profileDocs.some(d => d.type === 'syllabus');
    const hasBellSchedule = profileDocs.some(d => d.type === 'bell_schedule');
    const hasCalendar = profileDocs.some(d => d.type === 'school_calendar');
    const hasLessonTemplate = profileDocs.some(d => d.type === 'lesson_template');

    // Document limit (max 10 documents)
    const MAX_DOCS = 10;
    const canUploadMore = profileDocs.length < MAX_DOCS;

    // Notification reasons - based on actual uploaded docs and profile
    // (Currently computed for the incomplete profile check below)
    const _notifications = [
        !hasSyllabus && { id: 1, type: 'action', message: 'Upload your syllabus to personalize Saige', action: 'Upload' },
        !hasBellSchedule && { id: 2, type: 'action', message: 'Add your bell schedule for time-aware help', action: 'Upload' },
        !userProfile.grade && { id: 3, type: 'info', message: 'Set your grade level to calibrate content difficulty', action: 'Settings' },
        !hasCalendar && { id: 4, type: 'tip', message: 'Add your school calendar for deadline reminders', action: 'Upload' },
        !hasLessonTemplate && { id: 5, type: 'tip', message: 'Share your lesson plan template for faster planning', action: 'Upload' },
    ].filter(Boolean);

    // Delete document handler
    const handleDeleteDoc = async (docId) => {
        try {
            const res = await api.post('/delete-profile-doc', {
                email: userProfile.email,
                doc_id: docId
            });
            setProfileDocs(res.data.documents || []);
        } catch (e) {
            console.error('Failed to delete doc:', e);
        }
    };

    // Gentle engagement tips (no emojis)
    const tips = [
        "Try: \"Create a warm-up activity for tomorrow's class\"",
        "Upload your syllabus so I can personalize my help!",
        "I can help grade essays—just attach them and ask!",
        "Try: \"Give me 5 discussion questions about fractions\"",
        "Need a rubric? Just describe the assignment!",
        "Share your bell schedule for time-aware suggestions"
    ];

    // Get time-aware greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    // Rotate tips every 8 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            setTipIndex((prev) => (prev + 1) % tips.length);
        }, 8000);
        return () => clearInterval(timer);
    }, [tips.length]);

    // Check for missing documents (now uses profileDocs)
    useEffect(() => {
        const incomplete = !hasSyllabus || !hasBellSchedule;
        setHasIncompleteProfile(incomplete);
        if (incomplete) {
            setShowMissingDocs(true);
        }
    }, [profileDocs, hasSyllabus, hasBellSchedule]);

    // Close notifications when clicking outside
    const notificationRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        if (showNotifications) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showNotifications]);

    // Check if user is admin on load
    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                // Use /me endpoint (JWT-protected, returns is_admin from TenantContext)
                const response = await api.get('/me');
                setIsAdmin(response.data.is_admin === true);
            } catch {
                setIsAdmin(false);
            }
        };
        checkAdminStatus();
    }, [userProfile.email]);

    // Auto-focus input on load
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, isAiLoading]);

    // Dummy data for "Guide Mode" slides (connected to backend in future)
    const [slideIndex, setSlideIndex] = useState(0);
    const slides = [
        { title: "WRITING WORKSHOP", bullets: ["Chromebooks Open", "Canvas Module 4", "Review Feedback"], timer: "14:20" },
        { title: "BRAINSTORMING", bullets: ["Think-Pair-Share", "Draft Ideas", "Silence for 5 min"], timer: "05:00" },
        { title: "EXIT TICKET", bullets: ["Submit on Canvas", "Pack Up Quietly", "Wait for Bell"], timer: "02:00" }
    ];

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFiles(Array.from(e.target.files));
            // Focus back on input so they can type instructions for the file
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const clearFiles = () => {
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleChatSubmit = async () => {
        if (!chatInput.trim() && files.length === 0) return;

        const userQuery = chatInput;
        const attachedFiles = [...files];

        // Clear state immediately for UI responsiveness
        setChatInput('');
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';

        // Add to history
        setChatHistory(prev => [...prev, {
            role: 'user',
            text: userQuery,
            attachments: attachedFiles.map(f => f.name)
        }]);

        setIsAiLoading(true);

        try {
            // Use new /chat endpoint with JSON body and conversation history
            const response = await api.post('/chat', {
                email: userProfile.email,
                message: userQuery || "Please analyze my request.",
                history: chatHistory.map(msg => ({
                    role: msg.role === 'ai' ? 'ai' : 'user',
                    text: msg.text
                }))
            });

            const aiResponse = response.data.response_body || "Got it. I'm processing that now.";
            const emailSent = response.data.email_sent;

            // If email was sent, show simple confirmation instead of full content
            if (emailSent) {
                setChatHistory(prev => [...prev, {
                    role: 'ai',
                    text: "✅ Done! I've emailed you a Word document with the full content. Check your inbox!"
                }]);
            } else {
                setChatHistory(prev => [...prev, { role: 'ai', text: aiResponse }]);
            }

        } catch (error) {
            console.error("Chat failed", error);
            setChatHistory(prev => [...prev, { role: 'ai', text: "I'm having trouble reaching the Brain. Please check your connection." }]);
        } finally {
            setIsAiLoading(false);
        }
    };

    const nextSlide = () => setSlideIndex((prev) => (prev + 1) % slides.length);
    const prevSlide = () => setSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);

    return (
        <div className="h-screen w-full bg-[#FAFAFA] text-slate-900 font-sans overflow-hidden relative selection:bg-yellow-200">

            {/* Hidden File Input */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                multiple
            />

            {/* TOP BAR (Dynamic Island Style) - Only for Admins */}
            {!isGuideActive && isAdmin && (
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="absolute top-6 left-0 right-0 flex justify-center z-50 pointer-events-none"
                >
                    <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 pl-2 pr-4 py-2 rounded-full shadow-sm flex items-center gap-3 pointer-events-auto hover:shadow-md transition-shadow">
                        <button
                            onClick={() => setShowAdminDashboard(true)}
                            className="text-slate-400 hover:text-amber-500 transition-colors"
                            title="Admin Dashboard"
                        >
                            <Shield size={14} />
                        </button>
                    </div>
                </motion.div>
            )}

            {/* GUIDE MODE OVERLAY */}
            <AnimatePresence>
                {isGuideActive && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        className="absolute inset-0 z-[100] bg-slate-950 text-white flex flex-col"
                    >
                        {/* Cinema Header */}
                        <div className="p-8 flex justify-between items-center">
                            <div className="flex items-center gap-3 opacity-50">
                                <Clock size={18} />
                                <span className="font-mono tracking-widest text-sm">LIVE SESSION</span>
                            </div>
                            <button
                                onClick={() => setIsGuideActive(false)}
                                className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2"
                            >
                                <ChevronLeft size={16} /> Exit Guide
                            </button>
                        </div>

                        {/* Cinema Content */}
                        <div className="flex-1 flex items-center justify-center p-12">
                            <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                                <div className="space-y-8">
                                    <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none">
                                        {slides[slideIndex].title}
                                    </h1>
                                    <div className="space-y-6">
                                        {slides[slideIndex].bullets.map((b, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="flex items-center gap-4 text-2xl md:text-3xl font-light text-slate-300"
                                            >
                                                <CheckCircle2 className="text-[#FFC107] shrink-0" size={32} /> {b}
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-center md:justify-end">
                                    <div className="text-[12rem] md:text-[15rem] font-mono leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-600 opacity-80">
                                        {slides[slideIndex].timer}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cinema Controls */}
                        <div className="p-8 border-t border-white/10 flex justify-center gap-6">
                            <button onClick={prevSlide} className="w-16 h-16 rounded-full border border-white/20 flex items-center justify-center hover:bg-white text-white hover:text-slate-900 transition-all">
                                <ChevronLeft size={24} />
                            </button>
                            <button onClick={nextSlide} className="px-12 h-16 rounded-full bg-[#FFC107] text-slate-900 font-bold text-xl hover:bg-yellow-400 transition-all shadow-[0_0_40px_-10px_rgba(255,193,7,0.5)] flex items-center gap-3">
                                Next Slide <ArrowRight size={24} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>


            {/* MAIN INTERFACE (Zen Mode) */}
            {!isGuideActive && (
                <div className="h-full max-w-3xl mx-auto flex flex-col items-center justify-center p-6 relative z-10">

                    {/* Greeting / Empty State */}
                    <AnimatePresence>
                        {chatHistory.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="text-center mb-12 space-y-6"
                            >
                                <h1 className="text-5xl md:text-6xl font-medium text-slate-800 tracking-tight">
                                    {getGreeting()}, {userProfile.name.split(' ')[0].charAt(0).toUpperCase() + userProfile.name.split(' ')[0].slice(1).toLowerCase()}!
                                </h1>
                                <p className="text-xl text-slate-400 font-light">
                                    How can I help you today?
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Chat History View */}
                    {chatHistory.length > 0 && (
                        <div className="w-full flex-1 overflow-y-auto mb-8 p-4 space-y-6 mask-image-b">
                            {chatHistory.map((msg, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`max-w-[80%] p-6 rounded-3xl text-lg leading-relaxed shadow-sm flex flex-col gap-2 overflow-hidden break-words
                                    ${msg.role === 'user'
                                            ? 'bg-slate-100 text-slate-800 rounded-br-none'
                                            : 'bg-white border border-slate-100 text-slate-600 rounded-bl-none'
                                        }
                                `}>
                                        {/* Attachments if any */}
                                        {msg.attachments && msg.attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-2">
                                                {msg.attachments.map((f, i) => (
                                                    <div key={i} className="flex items-center gap-2 bg-white/50 px-3 py-1.5 rounded-lg text-sm text-slate-500 border border-slate-200/50">
                                                        <Paperclip size={12} /> {f}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {msg.role === 'ai' ? (
                                            <div className="w-full">
                                                <div className="prose prose-slate prose-sm max-w-none overflow-hidden break-words [overflow-wrap:anywhere] prose-headings:font-bold prose-headings:text-slate-800 prose-p:text-slate-600 prose-p:my-3 prose-li:text-slate-600 prose-strong:text-slate-700 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 [&>p]:mb-3 [&>p:last-child]:mb-0">
                                                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                                                </div>
                                                {/* Copy Button */}
                                                <button
                                                    onClick={() => handleCopyMessage(msg.text, idx)}
                                                    className="mt-3 flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-500 transition-colors"
                                                    title="Copy to clipboard"
                                                >
                                                    {copiedMessageId === idx ? (
                                                        <>
                                                            <Check size={14} className="text-green-500" />
                                                            <span className="text-green-500">Copied!</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy size={14} />
                                                            <span>Copy</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            msg.text
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                            {isAiLoading && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-slate-100 p-6 rounded-3xl rounded-bl-none shadow-sm flex items-center gap-3">
                                        <Loader2 className="animate-spin text-yellow-500" size={20} />
                                        <span className="text-slate-400 text-sm font-medium">Thinking...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}

                    {/* Input Pill (Spotlight) */}
                    <div className="w-full relative group">

                        {/* File Attachment Chip */}
                        {files.length > 0 && (
                            <div className="absolute -top-12 left-4 flex gap-2">
                                {files.map((f, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="bg-slate-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2"
                                    >
                                        <Paperclip size={14} className="text-[#FFC107]" />
                                        <span className="max-w-[150px] truncate">{f.name}</span>
                                        <button onClick={clearFiles} className="hover:text-red-400"><X size={14} /></button>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-r from-yellow-200 to-amber-200 rounded-full blur-xl opacity-20 group-focus-within:opacity-40 transition-opacity"></div>
                        <div className="relative bg-white border border-slate-200 shadow-2xl rounded-full p-2 pl-4 flex items-center gap-2 transition-all focus-within:ring-2 focus-within:ring-yellow-100 focus-within:border-yellow-300 focus-within:scale-[1.01]">

                            {/* Profile Icon - locked with input bar */}
                            <AccountDropdown
                                userProfile={userProfile}
                                onLogout={() => setShowLogoutConfirm(true)}
                                onUploadDocs={() => { setUploadSuccess(null); setShowDocUploadModal(true); }}
                                onProfileSettings={() => setShowProfileSettings(true)}
                                documents={profileDocs}
                                onDeleteDoc={handleDeleteDoc}
                            />

                            {/* Attach Button */}
                            <button
                                onClick={() => { setUploadSuccess(null); setShowDocUploadModal(true); }}
                                className="p-3 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                                title="Attach Document"
                            >
                                <Paperclip size={20} />
                            </button>

                            <div className="h-6 w-px bg-slate-100 mx-1"></div>

                            <input
                                ref={inputRef}
                                type="text"
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                                placeholder={files.length > 0 ? "Add instructions for these files..." : "Ask Saige to create..."}
                                className="flex-1 bg-transparent py-4 text-xl outline-none text-slate-800 placeholder:text-slate-300 font-medium"
                            />
                            <button
                                onClick={handleChatSubmit}
                                disabled={(!chatInput.trim() && files.length === 0) || isAiLoading}
                                className="p-3 bg-slate-900 rounded-full text-white hover:bg-slate-800 disabled:opacity-50 disabled:hover:bg-slate-900 transition-all"
                            >
                                <ArrowRight size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Dynamic Suggestions - rotate through tips when chat is empty */}
                    {chatHistory.length === 0 && (
                        <motion.div
                            key={tipIndex}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mt-6 text-center space-y-4"
                        >
                            <p className="text-sm text-slate-400 flex items-center justify-center gap-2">
                                <Lightbulb size={14} className="text-amber-400" />
                                {tips[tipIndex]}
                            </p>
                            {onSwitchToProduction && (
                                <button
                                    onClick={onSwitchToProduction}
                                    className="mt-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-sm font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg flex items-center gap-2 mx-auto"
                                >
                                    🎯 Switch to Production Mode
                                </button>
                            )}
                        </motion.div>
                    )}

                </div>
            )}



            {/* Evidence Analysis Modal - Hidden for now, keeping code for future use
            <EvidenceAnalysisModal
                isOpen={showEvidenceModal}
                onClose={() => setShowEvidenceModal(false)}
                userProfile={userProfile}
            />
            */}

            {/* Admin Dashboard Modal */}
            <AnimatePresence>
                {showAdminDashboard && isAdmin && (
                    <AdminDashboard
                        onClose={() => setShowAdminDashboard(false)}
                    />
                )}
            </AnimatePresence>

            {/* Logout Confirmation Modal */}
            <AnimatePresence>
                {showLogoutConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm"
                        onClick={() => setShowLogoutConfirm(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-bold text-slate-800 mb-2">Log out?</h3>
                            <p className="text-sm text-slate-500 mb-6">
                                Are you sure you want to log out of your account?
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowLogoutConfirm(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowLogoutConfirm(false);
                                        onLogout();
                                    }}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
                                >
                                    Log Out
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Profile Settings Modal */}
            <AnimatePresence>
                {showProfileSettings && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm"
                        onClick={() => setShowProfileSettings(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold text-slate-800">Profile Settings</h3>
                                <button
                                    onClick={() => setShowProfileSettings(false)}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Avatar & Name (editable) */}
                                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                                    <div className="w-14 h-14 bg-gradient-to-tr from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                                        {editName?.[0]?.toUpperCase() || 'T'}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className="w-full font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-amber-400 outline-none py-1 transition-colors"
                                            placeholder="Your name"
                                        />
                                        <p className="text-sm text-slate-500">{userProfile.email}</p>
                                    </div>
                                </div>

                                {/* Editable Fields */}
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Subject</label>
                                        <input
                                            type="text"
                                            value={editSubject}
                                            onChange={(e) => setEditSubject(e.target.value)}
                                            className="w-full mt-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                            placeholder="e.g., Math, English"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Grade Level</label>
                                        <input
                                            type="text"
                                            value={editGrade}
                                            onChange={(e) => setEditGrade(e.target.value)}
                                            className="w-full mt-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                            placeholder="e.g., 5th Grade"
                                        />
                                    </div>
                                </div>

                                {/* My Courses Section */}
                                <div className="mt-6 pt-6 border-t border-slate-100">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">My Courses</label>
                                        <button
                                            onClick={() => setShowAddCourse(!showAddCourse)}
                                            className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                                        >
                                            {showAddCourse ? 'Cancel' : '+ Add Course'}
                                        </button>
                                    </div>

                                    {/* Add Course Form */}
                                    {showAddCourse && (
                                        <div className="mb-4 p-3 bg-slate-50 rounded-xl space-y-2">
                                            <input
                                                type="text"
                                                placeholder="Course name (e.g., AP Biology)"
                                                value={newCourse.course_name}
                                                onChange={(e) => setNewCourse({ ...newCourse, course_name: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                            />
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-xs text-slate-400">Period</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g., 1st"
                                                        value={newCourse.period}
                                                        onChange={(e) => setNewCourse({ ...newCourse, period: e.target.value })}
                                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-xs text-slate-400">Grade</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g., 8th"
                                                        value={newCourse.grade_level}
                                                        onChange={(e) => setNewCourse({ ...newCourse, grade_level: e.target.value })}
                                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                    />
                                                </div>
                                                <div className="w-24">
                                                    <label className="text-xs text-slate-400">Length</label>
                                                    <input
                                                        type="number"
                                                        placeholder="50"
                                                        value={newCourse.period_length || ''}
                                                        onChange={(e) => setNewCourse({ ...newCourse, period_length: parseInt(e.target.value) || 50 })}
                                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleAddCourse}
                                                className="w-full py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                                            >
                                                Add Course
                                            </button>
                                        </div>
                                    )}

                                    {/* Course List */}
                                    {courses.length === 0 ? (
                                        <p className="text-sm text-slate-400 text-center py-4">No courses added yet</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {courses.map((course) => (
                                                <div
                                                    key={course.id}
                                                    className={`p-3 rounded-xl border ${course.is_primary ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-slate-50'}`}
                                                >
                                                    {editingCourse && editingCourse.id === course.id ? (
                                                        /* Edit Mode */
                                                        <div className="space-y-2">
                                                            <input
                                                                type="text"
                                                                value={editingCourse.course_name}
                                                                onChange={(e) => setEditingCourse({ ...editingCourse, course_name: e.target.value })}
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                                placeholder="Course name"
                                                            />
                                                            <div className="flex gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={editingCourse.period || ''}
                                                                    onChange={(e) => setEditingCourse({ ...editingCourse, period: e.target.value })}
                                                                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                                    placeholder="Period"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={editingCourse.grade_level || ''}
                                                                    onChange={(e) => setEditingCourse({ ...editingCourse, grade_level: e.target.value })}
                                                                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-amber-400 outline-none"
                                                                    placeholder="Grade"
                                                                />
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={handleUpdateCourse}
                                                                    className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                                                                >
                                                                    Save
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingCourse(null)}
                                                                    className="flex-1 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-300 transition-colors"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        /* Display Mode */
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="font-medium text-slate-800">{course.course_name}</p>
                                                                    {course.is_primary && (
                                                                        <span className="text-[10px] bg-amber-400 text-white px-1.5 py-0.5 rounded-full font-bold">PRIMARY</span>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-slate-500">
                                                                    {[course.period, course.grade_level].filter(Boolean).join(' • ') || 'No details'}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <button
                                                                    onClick={() => setEditingCourse({
                                                                        id: course.id,
                                                                        course_name: course.course_name,
                                                                        period: course.period || '',
                                                                        grade_level: course.grade_level || ''
                                                                    })}
                                                                    className="text-slate-400 hover:text-amber-500 p-1"
                                                                    title="Edit Course"
                                                                >
                                                                    ✏️
                                                                </button>
                                                                {!course.is_primary && (
                                                                    <button
                                                                        onClick={() => handleSetPrimary(course.id)}
                                                                        className="text-xs text-slate-400 hover:text-amber-500 px-2 py-1"
                                                                        title="Set as Primary"
                                                                    >
                                                                        ⭐
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => handleDeleteCourse(course.id)}
                                                                    className="text-slate-400 hover:text-red-500 p-1"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={() => {
                                            setShowDocUploadModal(true);
                                            setShowProfileSettings(false);
                                        }}
                                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Upload size={16} />
                                        Upload
                                    </button>
                                    <button
                                        onClick={() => {
                                            const updatedProfile = { ...userProfile, name: editName, subject: editSubject, grade: editGrade };
                                            localStorage.setItem('saige_profile', JSON.stringify(updatedProfile));
                                            onUpdateProfile?.(updatedProfile);
                                            setShowProfileSettings(false);
                                        }}
                                        className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Document Upload Modal */}
            <AnimatePresence>
                {showDocUploadModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm"
                        onClick={() => setShowDocUploadModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800">Upload Document</h3>
                                    <p className="text-xs text-slate-500">{profileDocs.length}/{MAX_DOCS} documents uploaded</p>
                                </div>
                                <button onClick={() => setShowDocUploadModal(false)} className="text-slate-400 hover:text-slate-600">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Success View */}
                            {uploadSuccess ? (
                                <div className="text-center py-6">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 mb-2">Upload Successful!</h4>
                                    <div className="bg-slate-50 rounded-xl p-4 mb-4 text-left">
                                        <p className="text-sm font-medium text-slate-600 mb-1">Classified as:</p>
                                        <p className="text-lg font-bold text-slate-800">{uploadSuccess.type}</p>
                                        <p className="text-sm text-slate-500 mt-2">{uploadSuccess.filename}</p>
                                        {uploadSuccess.summary && (
                                            <p className="text-sm text-slate-600 mt-2 italic">"{uploadSuccess.summary}"</p>
                                        )}
                                        <p className="text-xs text-slate-400 mt-2">{uploadSuccess.textLength.toLocaleString()} characters extracted</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setUploadSuccess(null)}
                                            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                                        >
                                            Upload Another
                                        </button>
                                        <button
                                            onClick={() => {
                                                setUploadSuccess(null);
                                                setShowDocUploadModal(false);
                                            }}
                                            className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            ) : !canUploadMore ? (
                                <div className="p-4 bg-amber-50 rounded-xl text-center">
                                    <p className="text-sm text-amber-700">You've reached the maximum of {MAX_DOCS} documents. Delete some to upload more.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-600 text-center">
                                        Upload any document and AI will automatically analyze it.
                                    </p>

                                    {/* Single Upload Button with FormData */}
                                    <label className="block cursor-pointer">
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept=".pdf,.doc,.docx,.txt,.csv"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;

                                                // Show analyzing state
                                                const container = e.target.nextElementSibling;
                                                const originalContent = container.innerHTML;
                                                container.innerHTML = `
                                                    <div class="flex flex-col items-center gap-3 py-6">
                                                        <div class="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                                                        <p class="text-sm font-medium text-slate-700">Uploading & Analyzing...</p>
                                                        <p class="text-xs text-slate-400">AI is reading your document</p>
                                                    </div>
                                                `;

                                                try {
                                                    // Validate we have email
                                                    if (!userProfile?.email) {
                                                        throw new Error('User email not found. Please log out and log back in.');
                                                    }

                                                    // Use FormData - server will handle everything
                                                    const formData = new FormData();
                                                    formData.append('email', userProfile.email);
                                                    formData.append('file', file);


                                                    // POST to new bulletproof endpoint
                                                    const response = await fetch('/upload-document', {
                                                        method: 'POST',
                                                        body: formData // No Content-Type header needed!
                                                    });

                                                    const data = await response.json();

                                                    if (!response.ok) {
                                                        throw new Error(data.detail || `Server error: ${response.status}`);
                                                    }

                                                    if (!data.success) {
                                                        throw new Error(data.message || 'Upload returned success: false');
                                                    }

                                                    // Update documents list
                                                    setProfileDocs(data.documents || []);

                                                    // Show success in modal
                                                    const typeLabels = {
                                                        syllabus: '📘 Syllabus',
                                                        bell_schedule: '🕐 Bell Schedule',
                                                        school_calendar: '📅 School Calendar',
                                                        lesson_template: '🧠 Lesson Template',
                                                        campus_handbook: '🛡️ Campus Handbook',
                                                        district_handbook: '⚙️ District Handbook',
                                                        roster: '👥 Roster',
                                                        curriculum: '📚 Curriculum',
                                                        other: '📄 Document'
                                                    };

                                                    setUploadSuccess({
                                                        type: typeLabels[data.detected_type] || data.detected_type,
                                                        summary: data.summary || '',
                                                        filename: data.filename,
                                                        textLength: data.text_length || 0
                                                    });

                                                } catch (err) {
                                                    console.error('Upload failed:', err);
                                                    container.innerHTML = originalContent;
                                                    alert('❌ Upload failed: ' + (err.message || 'Unknown error'));
                                                }

                                                // Clear input for next upload
                                                e.target.value = '';
                                            }}
                                        />
                                        <div className="p-8 rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-all text-center cursor-pointer">
                                            <Upload size={32} className="mx-auto mb-3 text-blue-500" />
                                            <p className="text-base font-semibold text-slate-700">Click to Upload Document</p>
                                            <p className="text-sm text-slate-500 mt-1">PDF, Word, or TXT files</p>
                                            <p className="text-xs text-slate-400 mt-3">AI will analyze and categorize automatically</p>
                                        </div>
                                    </label>
                                </div>
                            )}



                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Dashboard;
